import React from 'react';

import GrossVatTooltip from '~/components/Finances/GrossVatTooltip';
import { usePaymentsConfiguration } from '~/components/hooks/usePaymentsConfiguration';

import CardDialog from './CardDialog';
import { useClaim } from './ClaimContainer';
import { useCurrencyFormatter } from './CurrencyFormatterContext';
import PlainTable from './PlainTable';

// Paid/Incurred amount cell. Off: the plain formatted gross figure this table has always rendered.
// On: the net (excl. VAT/GST) figure, with a hover hint carrying the gross amount and the VAT/GST that
// was netted out of it. Reserves are recorded net already, so a reserve is never netted here and the
// difference between the two figures is purely the VAT/GST on the paid payments.
const renderAmountCell = ({ displayNetAmounts, currencyFormatter, grossAmount, netAmount }) => {
  if (!displayNetAmounts) {
    return currencyFormatter.format(grossAmount);
  }

  return (
    <GrossVatTooltip grossAmount={grossAmount} vatAmount={grossAmount - netAmount}>
      {currencyFormatter.format(netAmount)}
    </GrossVatTooltip>
  );
};

function WCClaimFinanceSummaryTable() {
  const { claim } = useClaim();
  const { currencyFormatter } = useCurrencyFormatter();
  const { displayNetAmounts } = usePaymentsConfiguration();

  const columnData = [
    { id: 'type', numeric: false, label: '' },
    {
      id: 'paid_to_date',
      numeric: false,
      label: 'Paid to date',
      specialCell: (exp) =>
        renderAmountCell({
          displayNetAmounts,
          currencyFormatter,
          grossAmount: exp.indemnity.paid_sum,
          netAmount: exp.indemnity.net_paid_sum,
        }),
    },
    {
      id: 'reserve_to_date',
      numeric: false,
      label: 'Reserve to date',
      specialCell: (exp) => currencyFormatter.format(exp.indemnity.reserve),
    },
    {
      id: 'incurred',
      numeric: false,
      label: 'Incurred',
      specialCell: (exp) =>
        renderAmountCell({
          displayNetAmounts,
          currencyFormatter,
          grossAmount: exp.indemnity.paid_sum + exp.indemnity.reserve,
          netAmount: exp.indemnity.net_paid_sum + exp.indemnity.reserve,
        }),
    },
  ];

  const getExposureByType = (type) => (exposure) => exposure.coverage_type === type;

  const medicalsExposure = claim.exposures.find(getExposureByType('coverage_wc_medicals'));
  const compensationExposure = claim.exposures.find(getExposureByType('coverage_wc_compensation'));
  const otherExpenseExposure = claim.exposures.find(getExposureByType('coverage_wc_other_expense'));
  const vocRehabExposure = claim.exposures.find(getExposureByType('coverage_wc_voc_rehab'));

  const medicalsRow = { ...medicalsExposure, type: 'Medicals' };
  const compensationRow = { ...compensationExposure, type: 'Composition' };
  const otherRow = { ...otherExpenseExposure, type: 'Other Expense' };
  const vocRehabRow = { ...vocRehabExposure, type: 'Voc. Rehab' };

  let rows = [medicalsRow, compensationRow, otherRow, vocRehabRow];
  rows = rows.map((row, idx) => ({ ...row, id: idx }));

  return (
    <>
      <CardDialog containerStyle={{ overflow: 'scroll' }} outlinedCard title="Finance summary table">
        <PlainTable columns={columnData} rows={rows} />
      </CardDialog>
    </>
  );
}

function ClaimFinanceSummaryTable() {
  const { claim } = useClaim();
  const { currencyFormatter } = useCurrencyFormatter();
  const { displayNetAmounts } = usePaymentsConfiguration();

  const columnData = [
    { id: 'type', numeric: false, label: '' },
    {
      id: 'paid_to_date',
      numeric: false,
      label: 'Paid to date',
      specialCell: (row) =>
        renderAmountCell({
          displayNetAmounts,
          currencyFormatter,
          grossAmount: row.paid_to_date,
          netAmount: row.net_paid_to_date,
        }),
    },
    {
      id: 'reserve_to_date',
      numeric: false,
      label: 'Reserve to date',
      specialCell: (row) => currencyFormatter.format(row.reserve_to_date),
    },
    {
      id: 'incurred',
      numeric: false,
      label: 'Incurred',
      specialCell: (row) =>
        renderAmountCell({
          displayNetAmounts,
          currencyFormatter,
          grossAmount: row.incurred,
          netAmount: row.net_incurred,
        }),
    },
  ];

  const indemnity_paid_sum = claim.exposures.reduce((acc, exposure) => acc + exposure.indemnity.paid_sum, 0);
  const expenses_paid_sum =
    claim.exposures.reduce((acc, exposure) => acc + exposure.expenses.paid_sum, 0) + claim.general_expenses.paid_sum;
  const indemnity_net_paid_sum = claim.exposures.reduce((acc, exposure) => acc + exposure.indemnity.net_paid_sum, 0);
  const expenses_net_paid_sum =
    claim.exposures.reduce((acc, exposure) => acc + exposure.expenses.net_paid_sum, 0) +
    claim.general_expenses.net_paid_sum;
  const indemnity_reserve = claim.exposures.reduce((acc, exposure) => acc + exposure.indemnity.reserve, 0);
  const expenses_reserve =
    claim.exposures.reduce((acc, exposure) => acc + exposure.expenses.reserve, 0) + claim.general_expenses.reserve;
  const indemnity = {
    type: 'Indemnity',
    paid_to_date: indemnity_paid_sum,
    net_paid_to_date: indemnity_net_paid_sum,
    reserve_to_date: indemnity_reserve,
    incurred: indemnity_paid_sum + indemnity_reserve,
    net_incurred: indemnity_net_paid_sum + indemnity_reserve,
  };
  const expenses = {
    type: 'Expenses',
    paid_to_date: expenses_paid_sum,
    net_paid_to_date: expenses_net_paid_sum,
    reserve_to_date: expenses_reserve,
    incurred: expenses_paid_sum + expenses_reserve,
    net_incurred: expenses_net_paid_sum + expenses_reserve,
  };
  const total = {
    type: 'Total',
    paid_to_date: expenses_paid_sum + indemnity_paid_sum,
    net_paid_to_date: expenses_net_paid_sum + indemnity_net_paid_sum,
    reserve_to_date: expenses_reserve + indemnity_reserve,
    incurred: expenses_paid_sum + expenses_reserve + indemnity_paid_sum + indemnity_reserve,
    net_incurred: expenses_net_paid_sum + expenses_reserve + indemnity_net_paid_sum + indemnity_reserve,
  };

  let rows = [indemnity, expenses, total];
  rows = rows.map((row, idx) => ({ ...row, id: idx }));

  return (
    <>
      <CardDialog containerStyle={{ overflow: 'scroll' }} outlinedCard title="Finance summary table">
        <PlainTable columns={columnData} rows={rows} />
      </CardDialog>
    </>
  );
}

export { WCClaimFinanceSummaryTable };
export default ClaimFinanceSummaryTable;
